/**
 * Property Creation Utilities
 * Functions to help with property creation form data transformation
 */

import type { CreatePropertyWithAdminRequest } from '@/types/api';

// Property creation form data interface (from create page)
export interface PropertyCreateFormData {
  // Property info
  name: string;
  property_type_id: string;
  stars: number;
  location: string;
  photoBase64?: string | null;

  // Contact information
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;

  // Contract settings
  contractScope: 'full' | 'limited';
  contractStartDate: string;
  contractEndDate: string;

  // Contract scope selection (department-touchpoint pairs)
  selectedDepartmentTouchpoints?: Array<{
    departmentId: string;
    touchpointIds: string[];
  }>;
}

/**
 * Property type name to ID mapping
 * These IDs should match the backend database
 */
export const PROPERTY_TYPE_MAP: Record<string, number> = {
  Hotel: 1,
  Restaurant: 2,
  Compound: 3,
  Store: 4,
};

/**
 * Role mapping from frontend to backend format
 */
export const ROLE_MAP: Record<string, string> = {
  admin: 'admin',
  manager: 'Manager',
  inspector: 'Auditor',
  client: 'Viewer',
};

/**
 * Convert property type name to ID
 */
export function getPropertyTypeIdByName(typeName: string): number {
  // Support case-insensitive lookups (frontend may provide 'hotel' or 'Hotel')
  const found = Object.entries(PROPERTY_TYPE_MAP).find(
    ([name]) => name.toLowerCase() === String(typeName).toLowerCase()
  );
  if (found) return found[1];
  throw new Error(`Unknown property type: ${typeName}`);
}

/**
 * Convert frontend role to backend role format
 */
export function mapRoleToBackend(role: string): string {
  return ROLE_MAP[role] || 'Viewer';
}

/**
 * Transform PropertyCreateFormData to CreatePropertyWithAdminRequest
 */
export async function transformFormDataToApiRequest(
  formData: PropertyCreateFormData
): Promise<CreatePropertyWithAdminRequest> {
  // Transform department-touchpoint pairs for scope
  const propertyScope =
    formData.selectedDepartmentTouchpoints?.flatMap(dept =>
      dept.touchpointIds.map(touchpointId => ({
        property_type_id: parseInt(formData.property_type_id),
        department_id: parseInt(dept.departmentId),
        touchpoint_id: parseInt(touchpointId),
      }))
    ) || [];

  // Transform the data to match backend expectations
  const request: CreatePropertyWithAdminRequest = {
    // Property fields
    name: formData.name,
    property_type_id: parseInt(formData.property_type_id),
    stars: formData.stars,
    location: formData.location,
    contact_person: formData.contactPerson,
    contact_email: formData.contactEmail,
    contact_phone: formData.contactPhone,
    contract_start: formData.contractStartDate,
    contract_end: formData.contractEndDate,
    is_active: true,
    photo_base64: formData.photoBase64 || undefined,

    // Property scope (only if limited access)
    property_scope:
      formData.contractScope === 'limited' ? propertyScope : undefined,
  };

  return request;
}

/**
 * Validate form data before submission
 */
export function validateFormData(formData: PropertyCreateFormData): string[] {
  const errors: string[] = [];

  // Property validation
  if (!formData.name.trim()) errors.push('Property name is required');
  if (!formData.property_type_id) errors.push('Property type is required');
  if (!formData.location.trim()) errors.push('Location is required');
  if (!formData.contactPerson.trim()) errors.push('Contact person is required');
  if (!formData.contactEmail.trim()) errors.push('Contact email is required');
  if (!formData.contactPhone.trim()) errors.push('Contact phone is required');

  // Contract dates validation
  if (!formData.contractStartDate)
    errors.push('Contract start date is required');
  if (!formData.contractEndDate) errors.push('Contract end date is required');
  if (
    formData.contractStartDate &&
    formData.contractEndDate &&
    formData.contractStartDate >= formData.contractEndDate
  ) {
    errors.push('Contract end date must be after start date');
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (formData.contactEmail && !emailRegex.test(formData.contactEmail)) {
    errors.push('Contact email format is invalid');
  }

  return errors;
}

/**
 * Get property type name from ID (for reverse lookup)
 */
export function getPropertyTypeNameById(id: number): string {
  const entry = Object.entries(PROPERTY_TYPE_MAP).find(
    ([, value]) => value === id
  );
  return entry ? entry[0] : 'Unknown';
}

/**
 * Get available property type options for UI
 */
export function getPropertyTypeOptions() {
  return Object.keys(PROPERTY_TYPE_MAP).map(name => ({
    value: name,
    label: name,
    id: PROPERTY_TYPE_MAP[name],
  }));
}

/**
 * Get available role options for UI
 */
export function getRoleOptions() {
  return Object.entries(ROLE_MAP).map(([key, value]) => ({
    value: key,
    label: value,
    backendValue: value,
  }));
}
